import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatJMD, isSoldOut, salePriceCents } from "@/lib/format";
import { ProductCard } from "@/components/ProductCard";
import { Countdown } from "@/components/Countdown";
import { usePromo } from "@/context/PromoContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, ShoppingBag, Share2 } from "lucide-react";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const promo = usePromo();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSize(null);
    setQty(1);
    setActiveImg(0);
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-32 pb-24 text-center">
        <p className="font-display text-4xl mb-4">TEE NAH DEH YAH</p>
        <Link to="/shop" className="font-mono text-xs uppercase text-jamaica-gold">Back to shop</Link>
      </div>
    );
  }
  if (!product) {
    return <div className="pt-32 pb-24 text-center font-mono text-sm text-zinc-500">Loading…</div>;
  }

  const images = [product.flagship_image, ...(product.gallery || [])].filter(Boolean);
  const soldOut = isSoldOut(product);
  const sizeInfo = product.sizes.find((s) => s.size === size);
  const maxStock = sizeInfo?.stock || 0;

  const handleAdd = () => {
    if (!size) {
      toast.error("Pick a size first, rude bwoy.");
      return;
    }
    if (maxStock <= 0) {
      toast.error("That size sold out.");
      return;
    }
    addItem({
      product_id: product.id,
      title: product.title,
      size,
      color: product.color,
      price_cents: product.price_cents,
      qty,
      image: product.flagship_image,
      slug: product.slug,
      maxStock,
    });
    toast.success(`${product.title} (${size}) added to bag`);
  };

  const shareProduct = async () => {
    const url = `${window.location.origin}/api/share/product/${product.slug || product.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: product.title, url }); } catch { /* user cancelled */ }
      return;
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied — paste it in WhatsApp or Instagram");
  };

  return (
    <div data-testid="product-page" className="pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button onClick={() => navigate(-1)} className="font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-jamaica-gold flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* GALLERY */}
          <div>
            <div className="relative aspect-[4/5] overflow-hidden bg-zinc-900 border border-white/10">
              <img src={images[activeImg]} alt={product.title} className={`w-full h-full object-cover ${soldOut ? "opacity-50 grayscale" : ""}`} />
              {soldOut && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-zinc-800 text-zinc-200 font-display text-2xl tracking-widest px-6 py-2 -rotate-6 border border-zinc-600">SOLD OUT</span>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`w-16 h-20 overflow-hidden border ${activeImg === i ? "border-jamaica-gold" : "border-white/10"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* DETAILS */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[10px] uppercase tracking-wider bg-secondary border border-border px-2 py-0.5">{product.fit} Fit</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">{product.color}</span>
              {product.limited_drop && <span className="bg-red-600 text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5">Limited Drop</span>}
            </div>

            <div className="flex items-start justify-between gap-3">
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">{product.title}</h1>
              <button
                onClick={shareProduct}
                data-testid="pp-share-btn"
                aria-label="Share this product"
                className="shrink-0 inline-flex items-center gap-1.5 border border-white/20 hover:border-jamaica-gold hover:text-jamaica-gold px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
            {promo.active && promo.sale_percent > 0 && product.status !== "draft" ? (
              <div className="flex items-baseline gap-3 mt-3">
                <p data-testid="pp-sale-price" className="font-mono text-2xl text-jamaica-gold">{formatJMD(salePriceCents(product.price_cents, promo.sale_percent))}</p>
                <p className="font-mono text-lg text-zinc-500 line-through">{formatJMD(product.price_cents)}</p>
                <span className="bg-jamaica-green text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5">{promo.sale_percent}% Off</span>
              </div>
            ) : (
              <p className="font-mono text-2xl text-jamaica-gold mt-3">{formatJMD(product.price_cents)}</p>
            )}

            {promo.active && promo.ends_at && (
              <div className="mt-3"><Countdown endsAt={promo.ends_at} /></div>
            )}

            {product.limited_drop && product.drop_ends_at && (
              <div className="mt-4"><Countdown endsAt={product.drop_ends_at} /></div>
            )}

            <p className="text-zinc-400 text-sm leading-relaxed mt-5 max-w-md">{product.description}</p>

            {/* SIZE SELECTOR */}
            <div className="mt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Select Size</p>
              <div className="grid grid-cols-5 gap-2 max-w-sm">
                {product.sizes.map((s) => {
                  const out = s.stock <= 0;
                  return (
                    <button
                      key={s.size}
                      data-testid={`size-${s.size}`}
                      disabled={out}
                      onClick={() => { setSize(s.size); setQty(1); }}
                      className={`h-12 border font-mono text-xs uppercase transition-colors relative ${
                        size === s.size
                          ? "bg-jamaica-gold text-black border-jamaica-gold"
                          : "bg-card border-border hover:border-jamaica-gold"
                      } ${out ? "opacity-30 cursor-not-allowed line-through" : ""}`}
                    >
                      {s.size}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* QTY + ADD */}
            {!soldOut && (
              <div className="flex items-center gap-3 mt-6">
                <div className="flex items-center border border-border">
                  <button data-testid="qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-12 flex items-center justify-center hover:text-jamaica-gold">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-mono text-sm w-10 text-center">{qty}</span>
                  <button data-testid="qty-inc" onClick={() => setQty((q) => Math.min(maxStock || 1, q + 1))} className="w-10 h-12 flex items-center justify-center hover:text-jamaica-gold">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <Button
                  data-testid="add-to-cart-btn"
                  onClick={handleAdd}
                  className="flex-1 h-12 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" /> Add To Bag
                </Button>
              </div>
            )}
            {soldOut && (
              <div className="mt-6 border border-zinc-700 bg-card p-4 text-center font-mono text-xs uppercase tracking-widest text-zinc-400" data-testid="soldout-notice">
                This tee sold out. Check back for restock.
              </div>
            )}
          </div>
        </div>

        {/* RELATED */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">YUH MIGHT LIKE</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
